import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  NAVIGATION_ITEM_MOVING_STRATEGIES,
  NavigationItemMovingStrategies
} from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-moving-strategies';
import {
  NavigationItemDragger,
  NavigationItemNode
} from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-dragger';
import {
  NAVIGATION_ITEM_FLAT_TREE_CONFIG,
  NavigationItemFlatTreeConfig
} from '@/app/platform/platform-shared/navigation-item-tree/types';
import { MatMenuTrigger } from '@angular/material/menu';
import { EgiPage } from '@ervo/page-framework';
import { NavigationItemTreeComponent } from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-tree-component';
import { Confirmer, CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { NAVIGATION_ITEM_ACTIONS, NavigationItemActions } from '@/app/platform/platform-data/actions/types';

// NOTE: in some cases, the nodes/navigationItems are mapped/cloned to a new object.
// This is because it seems like the data source makes all the objects immutable

@Component({
    selector: 'navigation-item-editor[app]',
    templateUrl: './navigation-item-editor.component.html',
    styleUrls: ['./navigation-item-editor.component.css'],
    providers: [],
    host: {
        class: 'bg-highlight dock-y d-flex flex-column'
    },
    standalone: false
})
export class NavigationItemEditorComponent
  extends NavigationItemTreeComponent<NavigationItemEntity, EgiAppEntity>
  implements OnInit
{
  @ViewChild('emptyItem', { static: true }) emptyItem: ElementRef;
  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger;

  navigationItemDragger: NavigationItemDragger;

  constructor(
    @Inject(NAVIGATION_ITEM_ACTIONS)
    private readonly navigationItemActions: NavigationItemActions,
    @Inject(NAVIGATION_ITEM_MOVING_STRATEGIES)
    private readonly navigationItemMovingStrategies: NavigationItemMovingStrategies,
    @Inject(NAVIGATION_ITEM_FLAT_TREE_CONFIG)
    public readonly navigationItemFlatTreeConfig: NavigationItemFlatTreeConfig<NavigationItemEntity>,
    @Inject(CONFIRMER)
    private readonly confirmer: Confirmer
  ) {
    super(navigationItemFlatTreeConfig, node => node.id);
  }

  menuTopLeftPosition = { x: '0', y: '0' };
  activeNode?: NavigationItemNode<NavigationItemEntity>;
  nodeToConfigure?: NavigationItemNode<NavigationItemEntity>;

  updating = false;
  showConfigureNavigationItemDialog = false;

  ngOnInit() {
    this.navigationItemDragger = new NavigationItemDragger(
      this.emptyItem,
      this.treeControl,
      this.navigationItemMovingStrategies
    );
  }

  handleDragStart(event: DragEvent, node: NavigationItemNode<NavigationItemEntity>) {
    this.navigationItemDragger.startDrag(event, node);
    this.treeControl.collapse(node);
  }

  handleDragOver(event: any, node: NavigationItemNode<NavigationItemEntity>) {
    if (node.navigationItem.isClickTroughPage) {
      return;
    }
    event.preventDefault();
    this.navigationItemDragger.dragOver(event, node);
  }

  handleDrop(event, targetNode: NavigationItemNode<NavigationItemEntity>) {
    event.preventDefault();
    if (!this.navigationItemDragger.isSameAsDraggedNode(targetNode)) {
      const movedNode = this.navigationItemDragger.moveNode(targetNode);
      this.navigationItemActions.move(movedNode);
    }
    this.navigationItemDragger.endDrag();
  }

  handleDragEnd() {
    this.navigationItemDragger.endDrag();
  }

  async onNewItemDropped(
    $event: CdkDragDrop<EgiPage>,
    targetNode: NavigationItemNode<NavigationItemEntity>
  ) {
    targetNode.entered = false;

    const page = $event.item.data as EgiPage;
    const navigationItem = NavigationItemEntity.createFromPage(page, targetNode.navigationItem);

    const result = await this.navigationItemActions.create(navigationItem);
    result.ifSuccess(() => {
      this.treeControl.expand(targetNode);
    });
  }

  async deleteNavigationItem(node: NavigationItemNode<NavigationItemEntity>) {
    const confirmation = await this.confirmer.confirm(
      'Bent u zeker dat u dit item wil verwijderen?'
    );
    if (confirmation) {
      this.indicateUpdating();
      const result = await this.navigationItemActions.delete(node.navigationItem);
      this.indicateNotUpdating();
    }
  }

  insertSubmenu(node: NavigationItemNode<NavigationItemEntity>) {
    this.treeControl.expand(node);
    const subMenu = NavigationItemEntity.createMenu(node.navigationItem);
    const root = this.app.rootNavigationItem.clone();
    const parent = root.findDescendant(node.id);
    parent.addChild(subMenu);
    this.dataSource.data = [root];
  }

  async createSubMenu(node: NavigationItemNode<NavigationItemEntity>, name: string) {
    const item = NavigationItemEntity.createDefault();
    item.name = name;
    item.parentId = node.navigationItem.parentId;
    item.appId = node.navigationItem.appId;
    this.indicateUpdating();
    const result = await this.navigationItemActions.createMenuItem(item);
    this.indicateNotUpdating();
    result.ifSuccess(() => {});
  }

  onMenuClose() {
    this.activeNode = undefined;
  }

  editNode(node: NavigationItemNode<NavigationItemEntity>) {
    node.editing = true;
  }

  async saveName(node: NavigationItemNode<NavigationItemEntity>, name: string) {
    node.navigationItem.name = name;
    this.indicateUpdating();
    const result = await this.navigationItemActions.update(node.navigationItem);
    this.indicateNotUpdating();
  }

  onNewItemDragEnter(node: NavigationItemNode<NavigationItemEntity>) {
    node.entered = true;
  }

  onNewItemDragLeave(node: NavigationItemNode<NavigationItemEntity>) {
    node.entered = false;
  }

  onConfigure(node: NavigationItemNode<NavigationItemEntity>) {
    this.nodeToConfigure = node;
    this.showConfigureNavigationItemDialog = true;
  }

  closeConfigurationDialog(): void {
    this.showConfigureNavigationItemDialog = false;
  }

  private indicateUpdating(): void {
    this.updating = true;
  }

  private indicateNotUpdating(): void {
    this.updating = false;
  }
}
